import { useState } from "react";
import { useAction, useQuery as useConvexQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { reportWriteFailure } from "@/lib/reportWriteFailure";
import { Link } from "wouter";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import { GlassCard, Btn, PillTag } from "@/components/mnf/UI";
import Icon from "@/components/mnf/Icon";

/**
 * Billing
 *
 * EVERY value on this screen comes from `users.mySubscription`, which reads the
 * Convex user row the Stripe webhook writes. Nothing here is read from the
 * in-memory demo store any more.
 *
 * It used to be. `const userId = me?.id ?? <seeded demo account id>` fed a store lookup whose
 * only row is the demo user, so a real subscriber saw no plan, no renewal date
 * and no cancel button — and if ensureDomainId ever failed she was shown
 * HANNAH's membership (founding member, card ending 4242) with the plan chooser
 * hidden, so she could not pay at all. The fallback is gone; there is no demo
 * identity left to fall back TO.
 *
 * What is deliberately NOT shown: price charged, card last four, member-since,
 * and the renewal-reminder schedule. Those existed only as demo-store fiction —
 * no Stripe field behind them and no job that sends the reminders. A billing
 * screen may show nothing; it may not show something made up.
 */

type MySubscription = {
  tier: string | null;
  status: string | null;
  hasStripeRecord: boolean;
  cancelAtPeriodEnd: boolean | null;
  currentPeriodEnd: number | null;
};

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function daysUntil(ts: number): number {
  return Math.max(0, Math.ceil((ts - Date.now()) / (1000 * 60 * 60 * 24)));
}

/** Plain-English status. Unknown values fall through rather than being hidden. */
function statusLabel(status: string | null): string {
  switch (status) {
    case "active": return "Active";
    case "trialing": return "Free trial";
    case "past_due": return "Payment overdue";
    case "incomplete": return "Awaiting payment";
    case "cancelled": return "Cancelled";
    default: return "Unknown";
  }
}


/**
 * ChoosePlan — the cash register.
 *
 * Six components gated features by tier and Billing offered only cancel and
 * reactivate, so a sister who wanted to pay had no way to. This calls the
 * authenticated Convex action, which builds the Stripe Checkout session
 * server-side and returns a URL.
 *
 * Prices are NOT set here. Price IDs live in Convex env vars and are resolved
 * server-side, so what a customer is charged can never disagree with Stripe.
 * The numbers below are display copy; changing them changes no charge.
 */
function ChoosePlan({ currentTier }: { currentTier: string | null }) {
  const createCheckout = useAction(api.stripe.createCheckout);
  const [cycle, setCycle] = useState<"monthly" | "annual">("monthly");
  const [busy, setBusy] = useState<string | null>(null);

  const plans = [
    { tier: "Seedling" as const, monthly: 47, annual: 456, blurb: "Start tending." },
    { tier: "Rooted" as const, monthly: 97, annual: 936, blurb: "Grow with a coach." },
    { tier: "Flourishing" as const, monthly: 197, annual: 1896, blurb: "Everything, walked together." },
  ];

  const go = (tier: "Seedling" | "Rooted" | "Flourishing") => {
    if (busy) return;
    setBusy(tier);
    void createCheckout({ tier, cycle })
      .then((r: { url: string | null }) => {
        if (!r?.url) throw new Error("Stripe returned no checkout URL");
        window.location.href = r.url;
      })
      .catch((err: unknown) => {
        setBusy(null);
        reportWriteFailure(
          "billing", `starting ${tier} checkout`, err,
          "We couldn't open checkout",
          "Nothing has been charged. Check your connection and try again.",
        );
      });
  };

  return (
    <div className="px-5 mt-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] mnf-body uppercase" style={{ color: "var(--mnf-tg)", letterSpacing: "0.1em" }}>
          {currentTier ? "Change your plan" : "Choose your plan"}
        </p>
        <div className="flex items-center gap-1">
          {(["monthly", "annual"] as const).map((c) => (
            <button
              key={c}
              onClick={() => setCycle(c)}
              data-testid={`button-cycle-${c}`}
              className="text-[11px] mnf-body px-2.5 py-1 rounded-full"
              style={{
                background: cycle === c ? "var(--mnf-fd)" : "transparent",
                color: cycle === c ? "#fff" : "var(--mnf-tg)",
                border: "1px solid rgba(30,74,53,0.16)",
              }}
            >
              {c === "annual" ? "Annual · save ~19%" : "Monthly"}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {plans.map((pl) => {
          const isCurrent = currentTier === pl.tier;
          const amount = cycle === "annual" ? pl.annual : pl.monthly;
          return (
            <GlassCard key={pl.tier} organic padding={14}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="mnf-serif" style={{ fontSize: 16, fontWeight: 500, color: "var(--mnf-fd)" }}>
                      {pl.tier}
                    </p>
                    {isCurrent && <PillTag tone="forest">Current</PillTag>}
                  </div>
                  <p className="text-xs mnf-body mt-0.5" style={{ color: "var(--mnf-tg)" }}>
                    ${amount}/{cycle === "annual" ? "year" : "month"} · {pl.blurb}
                  </p>
                </div>
                <Btn
                  variant={isCurrent ? "ghost" : "primary"}
                  size="sm"
                  disabled={isCurrent || busy !== null}
                  onClick={() => go(pl.tier)}
                  data-testid={`button-choose-${pl.tier.toLowerCase()}`}
                >
                  {isCurrent ? "Current" : busy === pl.tier ? "Opening…" : "Choose"}
                </Btn>
              </div>
            </GlassCard>
          );
        })}
      </div>

      <p className="text-[11px] mnf-body mt-2" style={{ color: "var(--mnf-tg)" }}>
        Secure checkout by Stripe. Cancel anytime — you keep access through the end of the paid period.
      </p>
    </div>
  );
}


/**
 * Cancel / re-activate.
 *
 * The button this replaces called `mutations.setCancelAtPeriodEnd`, an
 * in-memory store write. Stripe was never told. A sister who cancelled was
 * shown "Your membership is ending" and went on being charged every month,
 * while the checkout copy above promised "Cancel anytime". That is the exact
 * failure this codebase keeps producing: a control that reports success for
 * work never performed.
 *
 * Now it calls a Convex action that calls Stripe. The UI state does not move
 * until Stripe has confirmed, because the action writes the record only after
 * `subscriptions.update` returns and this component re-reads that record.
 * If Stripe rejects it, she is TOLD and the screen keeps saying "active".
 */
function CancelControls({ sub }: { sub: MySubscription }) {
  const setCancel = useAction(api.stripe.setCancelAtPeriodEnd);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  // No subscription id on file means there is nothing we could cancel. Showing
  // a cancel button here would be another control that cannot do its job.
  if (!sub.hasStripeRecord) {
    return (
      <GlassCard organic padding={14}>
        <div className="flex items-start gap-3">
          <Icon name="info" size={20} color="#A07820" />
          <p className="mnf-body flex-1" style={{ color: "var(--mnf-body)", fontSize: 12.5, lineHeight: 1.45 }}>
            We do not have a Stripe subscription on file for this account, so we
            cannot change your billing from here. Email support and we will sort
            it out by hand.
          </p>
        </div>
      </GlassCard>
    );
  }

  const run = (cancel: boolean) => {
    if (busy) return;
    setBusy(true);
    void setCancel({ cancel })
      .then(() => {
        // Nothing to set locally. `users.mySubscription` is reactive and the
        // action has already written Stripe's CONFIRMED answer to it.
        setConfirming(false);
      })
      .catch((err: unknown) => {
        reportWriteFailure(
          "billing",
          cancel ? "scheduling cancellation" : "re-activating membership",
          err,
          cancel ? "We could not cancel your membership" : "We could not re-activate",
          cancel
            ? "Nothing has changed and your next payment is still scheduled. Please try again, or email support."
            : "Your membership is still set to end. Please try again, or email support.",
        );
      })
      .finally(() => setBusy(false));
  };

  if (sub.cancelAtPeriodEnd) {
    return (
      <GlassCard organic padding={14}>
        <div className="flex items-start gap-3">
          <Icon name="info" size={20} color="#A07820" />
          <div className="flex-1 min-w-0">
            <p className="mnf-serif" style={{ color: "var(--mnf-f)", fontSize: 14.5, fontWeight: 600, lineHeight: 1.3 }}>
              Your membership is ending
            </p>
            <p className="mnf-body mt-1" style={{ color: "var(--mnf-body)", fontSize: 12.5, lineHeight: 1.45 }}>
              {sub.currentPeriodEnd
                ? <>You keep full access through <strong>{formatDate(sub.currentPeriodEnd)}</strong>, and you will not be charged again.</>
                : <>You keep full access through the end of the period you have already paid for, and you will not be charged again.</>}
              {" "}Change your mind? Re-activate any time before then.
            </p>
            <div className="mt-3">
              <Btn
                variant="primary"
                disabled={busy}
                onClick={() => run(false)}
                data-testid="button-reactivate"
              >
                {busy ? "Re-activating…" : "Re-activate membership"}
              </Btn>
            </div>
          </div>
        </div>
      </GlassCard>
    );
  }

  if (confirming) {
    return (
      <GlassCard organic padding={14}>
        <p className="mnf-serif" style={{ color: "var(--mnf-f)", fontSize: 14.5, fontWeight: 600, lineHeight: 1.3 }}>
          Are you sure?
        </p>
        <p className="mnf-body mt-1" style={{ color: "var(--mnf-body)", fontSize: 12.5, lineHeight: 1.45 }}>
          {sub.currentPeriodEnd
            ? <>We will not charge you on {formatDate(sub.currentPeriodEnd)}, and you keep full access until then.</>
            : <>You will not be charged again, and you keep full access through the end of the period you have already paid for.</>}
        </p>
        <div className="mt-3 flex gap-2">
          <Btn
            variant="coral"
            disabled={busy}
            onClick={() => run(true)}
            data-testid="button-confirm-cancel"
          >
            {busy ? "Cancelling…" : "Yes, cancel at period end"}
          </Btn>
          <Btn
            variant="ghost"
            disabled={busy}
            onClick={() => setConfirming(false)}
            data-testid="button-keep-membership"
          >
            Keep my membership
          </Btn>
        </div>
      </GlassCard>
    );
  }

  return (
    <Btn
      variant="ghost"
      block
      onClick={() => setConfirming(true)}
      data-testid="button-cancel-membership"
    >
      Cancel membership
    </Btn>
  );
}

export default function Billing() {
  // The ONE source of truth for this screen: the Convex row the Stripe webhook
  // writes. `undefined` means still loading — distinct from `null`, which means
  // loaded and she has no subscription.
  const sub = useConvexQuery(api.users.mySubscription, {});
  const justPaid = new URLSearchParams(window.location.search).get("checkout") === "success";

  const thankYou = justPaid && (
    <div className="px-5 mt-2">
      <GlassCard organic padding={14}>
        <p className="mnf-serif" style={{ fontSize: 16, fontWeight: 500, color: "var(--mnf-fd)" }}>
          Thank you, sister 🌿
        </p>
        <p className="text-xs mnf-body mt-1" style={{ color: "var(--mnf-tg)" }}>
          {sub?.tier
            ? `Your ${sub.tier} membership is active.`
            : "Your payment went through. Your membership is being activated — this can take a few seconds."}
        </p>
      </GlassCard>
    </div>
  );

  // Still loading. Do NOT render the plan chooser here: telling a paying member
  // to "choose your plan" for a beat, every time she opens this screen, is the
  // same lie in a shorter window.
  if (sub === undefined) {
    return (
      <div className="pb-24" data-testid="page-billing">
        <ScreenHeader variant="forest" eyebrow="Membership" title="Billing" subtitle="Loading your membership…" />
      </div>
    );
  }

  // Loaded, and she has no tier — the person who needs to see prices.
  if (!sub || !sub.tier) {
    return (
      <div className="pb-24" data-testid="page-billing">
        <ScreenHeader
          variant="forest"
          eyebrow="Membership"
          title="Billing"
          subtitle="Choose the plan that fits your season."
        />
        {thankYou}
        <ChoosePlan currentTier={null} />
      </div>
    );
  }

  const daysToRenewal = sub.currentPeriodEnd ? daysUntil(sub.currentPeriodEnd) : null;

  return (
    <div className="pb-24" data-testid="page-billing">
      <ScreenHeader
        variant="forest"
        eyebrow="Membership"
        title="Billing"
        subtitle="Manage your subscription."
      />

      {thankYou}

      {/* Current plan */}
      <div className="px-5 mt-2">
        <GlassCard organic padding={16}>
          <div className="flex items-center gap-2 mb-1.5">
            <PillTag tone="forest">{sub.tier}</PillTag>
            <PillTag tone="neutral">{statusLabel(sub.status)}</PillTag>
            {sub.cancelAtPeriodEnd && <PillTag tone="coral">Ending soon</PillTag>}
          </div>

          {sub.status === "past_due" && (
            <p className="mnf-body mt-1" style={{ color: "#B4462F", fontSize: 12.5, lineHeight: 1.45 }}>
              Your last payment did not go through. Stripe will retry, and your
              access stays on for now — but please update your card to avoid
              losing it.
            </p>
          )}

          <div style={{ height: 1, background: "rgba(45,62,48,0.1)", margin: "14px 0 12px" }} />

          <div className="flex items-center justify-between">
            <span className="mnf-body" style={{ color: "var(--mnf-body)", fontSize: 13 }}>
              {sub.cancelAtPeriodEnd ? "Access ends" : "Next renewal"}
            </span>
            <span
              className="mnf-serif"
              style={{ color: "var(--mnf-f)", fontSize: 13, fontWeight: 600 }}
              data-testid="text-next-renewal"
            >
              {sub.currentPeriodEnd === null ? (
                /* We have not been told the date yet — say so. A placeholder
                   date on a billing screen is a false statement about money. */
                <span style={{ color: "rgba(45,62,48,0.55)", fontWeight: 400 }}>Not available yet</span>
              ) : (
                <>
                  {formatDate(sub.currentPeriodEnd)}
                  {daysToRenewal !== null && (
                    <span style={{ color: "rgba(45,62,48,0.55)", fontWeight: 400, marginLeft: 6 }}>
                      ({daysToRenewal} {daysToRenewal === 1 ? "day" : "days"})
                    </span>
                  )}
                </>
              )}
            </span>
          </div>
        </GlassCard>
      </div>

      <ChoosePlan currentTier={sub.tier} />

      {/* Cancel flow */}
      <div className="px-5 mt-3">
        <CancelControls sub={sub} />
      </div>

      {/* Legal pointer */}
      <div className="px-5 mt-4 text-center">
        <Link
          href="/legal/subscription"
          className="mnf-body inline-flex items-center gap-1"
          style={{
            color: "var(--mnf-tg)",
            fontSize: 12,
            opacity: 0.75,
            textDecoration: "none",
          }}
          data-testid="link-subscription-policy"
        >
          <Icon name="description" size={13} color="var(--mnf-tg)" />
          Subscription and refund policy
        </Link>
      </div>
    </div>
  );
}
